import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  finishOccurrenceGroupOnBehalf,
  revertOccurrenceGroupToPlanned,
} from "@/api/Activities";
import {
  deducedError,
  isErrorWithKnownErrorCode,
  knownErrorCodeSchema,
  useErrorMessageAlert,
} from "@/Utils/ErrorUtils";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import {
  getGroupOrVisitStatus,
  visitStatusSchema,
  type IFinishableGroupStatus,
  type IGroupOfActivityOccurrence,
  type IRevertableVisitStatus,
  type IUnfinishedVisitStatus,
} from "@medoma/models/activities";
import { useLingui } from "@lingui/react/macro";
import type { IVisit } from "@medoma/models/visits";
import { min } from "date-fns";
import { isScheduledForFuture } from "./markAsFinishedUtils";

type IFinishableGroup = IGroupOfActivityOccurrence & {
  visitStatus?: IFinishableGroupStatus | null;
};

export type IFinishableVisit = StrictOmit<IVisit, "status"> & {
  status: IUnfinishedVisitStatus;
};

export type IFinishableGroupOrVisit = IFinishableGroup | IFinishableVisit;

type IRevertableGroup = IGroupOfActivityOccurrence & {
  visitStatus: IRevertableVisitStatus;
};

type IRevertableVisit = StrictOmit<IVisit, "status"> & {
  status: IRevertableVisitStatus;
};

export type IRevertableGroupOrVisit = IRevertableGroup | IRevertableVisit;

export const useMarkGroupAsFinished = () => {
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { alert, confirm } = useDialog();
  const { t } = useLingui();

  const {
    mutate: markGroupAsFinishedMutation,
    isPending: isMarkingGroupAsFinished,
  } = useMutation({
    mutationFn: (group: IFinishableGroupOrVisit) =>
      finishOccurrenceGroupOnBehalf({
        id: group.id,
        occurrenceIds: group.occurrences.map((occurrence) => occurrence.id),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att markera besöket som utfört.`} ${deducedError(error)}`,
      );
    },
  });

  const markGroupAsFinished = async (group: IFinishableGroupOrVisit) => {
    const { assignees } = group;

    // Don't allow marking as finished if there are no assignees
    // Since we need to know who did the activity
    if (assignees.length === 0) {
      return alert({
        title: t`Besöket har inte tilldelats någon`,
        message: t`Det går inte att markera besöket som utfört eftersom det inte har några tilldelade medarbetare.`,
      });
    }

    const scheduledStart =
      "start" in group
        ? group.start
        : min(group.occurrences.map((occurrence) => occurrence.start));

    const confirmedFinish = await confirm({
      title: t`Markera som utfört`,
      message: isScheduledForFuture(scheduledStart)
        ? t`Besöket är planerat att utföras i framtiden. Är du säker på att du vill markera det som utfört? Alla aktiviteter i besöket kommer att markeras som utförda.`
        : t`Är du säker på att du vill markera besöket som utfört? Alla aktiviteter i besöket kommar att markeras som utförda.`,
    });

    if (confirmedFinish) {
      markGroupAsFinishedMutation(group);
    }
  };

  const {
    mutate: revertGroupToPlannedMutation,
    isPending: isRevertingGroupToPlanned,
  } = useMutation({
    mutationFn: (group: IRevertableGroupOrVisit) =>
      revertOccurrenceGroupToPlanned(group.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
    onError: async (error) => {
      // Someone else already reverted it — refresh silently so the UI catches up.
      if (
        isErrorWithKnownErrorCode(error) &&
        error.data.code === knownErrorCodeSchema.enum.AlreadyPlanned
      ) {
        await queryClient.invalidateQueries();
        return;
      }
      displayErrorMessageAlert(
        `${t`Det gick inte att ångra besöket.`} ${deducedError(error)}`,
      );
    },
  });

  const getRevertConfirmMessage = (status: IRevertableVisitStatus) => {
    switch (status) {
      case visitStatusSchema.enum.finished:
        return t`Är du säker på att du vill ångra det utförda besöket? Besöket återgår till planerat.`;
      case visitStatusSchema.enum.ongoing:
        return t`Är du säker på att du vill avbryta det påbörjade besöket? Besöket återgår till planerat.`;
      case visitStatusSchema.enum.travellingTo:
        return t`Är du säker på att du vill avbryta den påbörjade resan? Besöket återgår till planerat.`;
      default:
        throw status satisfies never;
    }
  };

  const revertGroupToPlanned = async (group: IRevertableGroupOrVisit) => {
    const status = getGroupOrVisitStatus(group) as IRevertableVisitStatus;
    const confirmed = await confirm({
      title: t`Ångra besök`,
      message: getRevertConfirmMessage(status),
    });

    if (confirmed) {
      revertGroupToPlannedMutation(group);
    }
  };

  return {
    markGroupAsFinished,
    isMarkingGroupAsFinished,
    revertGroupToPlanned,
    isRevertingGroupToPlanned,
  };
};
